import type { DataTableColumn, InputTableColumn } from '@/components/custom/erp';
import { formatDate, formatMoney, roundNumber } from '@/lib/formatUtils';
import type { InvoiceAllocationRow, ReceiptRow } from './hooks';

const formatRoundedNumber = (value: number | string) => formatMoney(roundNumber(value));

export const getReceiptColumns = (onDocumentClick: (row: ReceiptRow) => void): DataTableColumn<ReceiptRow>[] => [
  {
    field: 'so_ct',
    headerName: 'Số phiếu thu',
    width: 140,
    renderCell: params => (
      <button
        type='button'
        className='cursor-pointer bg-transparent p-0 text-left hover:text-blue-500 hover:underline'
        onClick={event => {
          event.stopPropagation();
          onDocumentClick(params.row);
        }}
      >
        {params.row.so_ct || ''}
      </button>
    )
  },
  { field: 'ngay_ct', headerName: 'Ngày phiếu thu', width: 120 },
  { field: 'ma_kh', headerName: 'Mã khách hàng', width: 150 },
  { field: 'ten_kh', headerName: 'Tên khách hàng', width: 200 },
  { field: 'dien_giai', headerName: 'Diễn giải', width: 250 },
  { field: 'ma_nt', headerName: 'Ngoại tệ', width: 100 },
  {
    field: 'ty_gia',
    headerName: 'Tỷ giá',
    width: 120,
    type: 'number',
    align: 'right',
    renderCell: params => Number(params.row.ty_gia || 0)
  },
  { field: 'tk_co', headerName: 'Tài khoản', width: 120 },
  {
    field: 'tien_nt',
    headerName: 'Tiền thu',
    width: 150,
    type: 'number',
    align: 'right',
    renderCell: params => Number(params.row.tien_nt || 0)
  },
  {
    field: 'da_pb_nt',
    headerName: 'Đã phân bổ',
    width: 150,
    type: 'number',
    align: 'right',
    renderCell: params => Number(params.row.da_pb_nt || 0)
  },
  { field: 'ma_ct', headerName: 'Chứng từ', width: 120 }
];

export const getInputTableColumns = (
  onToggleSelection: (uuid: string, selected: boolean) => void,
  onDocumentClick: (row: InvoiceAllocationRow) => void
): InputTableColumn[] => [
  {
    field: 'selected',
    headerName: '...',
    width: 44,
    sortable: false,
    filterable: false,
    align: 'center',
    renderCell: params => {
      const row = params.row as InvoiceAllocationRow;

      return (
        <input
          type='checkbox'
          aria-label={`Chọn hóa đơn ${row.so_ct}`}
          checked={row.selected}
          className='size-4 cursor-pointer accent-[#1976d2]'
          onClick={event => event.stopPropagation()}
          onChange={event => onToggleSelection(row.uuid, event.target.checked)}
        />
      );
    }
  },
  {
    field: 'stt',
    headerName: 'Stt',
    width: 50,
    type: 'number',
    align: 'center'
  },
  {
    field: 'so_ct',
    headerName: 'Số hóa đơn',
    width: 150,
    renderCell: params => {
      const row = params.row as InvoiceAllocationRow;

      return (
        <button
          type='button'
          className='cursor-pointer bg-transparent p-0 text-left hover:text-blue-500 hover:underline'
          onClick={event => {
            event.stopPropagation();
            onDocumentClick(row);
          }}
        >
          {row.so_ct}
        </button>
      );
    }
  },
  {
    field: 'ngay_ct',
    headerName: 'Ngày hóa đơn',
    width: 120,
    renderCell: params => formatDate((params.row as InvoiceAllocationRow).ngay_ct)
  },
  {
    field: 'ty_gia',
    headerName: 'Tỷ giá',
    width: 100,
    type: 'number',
    align: 'right',
    renderCell: params => formatRoundedNumber((params.row as InvoiceAllocationRow).ty_gia)
  },
  {
    field: 't_tt_nt',
    headerName: 'Tổng tiền',
    width: 150,
    type: 'number',
    align: 'right',
    renderCell: params => formatRoundedNumber((params.row as InvoiceAllocationRow).t_tt_nt)
  },
  {
    field: 'da_pb_nt',
    headerName: 'Đã phân bổ',
    width: 150,
    type: 'number',
    align: 'right',
    renderCell: params => formatRoundedNumber((params.row as InvoiceAllocationRow).da_pb_nt)
  },
  {
    field: 'da_pb',
    headerName: 'Đã phân bổ VND',
    width: 150,
    type: 'number',
    align: 'right',
    renderCell: params => formatRoundedNumber((params.row as InvoiceAllocationRow).da_pb)
  },
  {
    field: 'da_pb_ct_nt',
    headerName: 'P/bổ trên pthu',
    width: 150,
    type: 'number',
    align: 'right',
    renderCell: params => formatRoundedNumber((params.row as InvoiceAllocationRow).da_pb_ct_nt)
  },
  {
    field: 'da_pb_ct',
    headerName: 'P/bổ trên pthu VND',
    width: 150,
    type: 'number',
    align: 'right',
    renderCell: params => formatRoundedNumber((params.row as InvoiceAllocationRow).da_pb_ct)
  },
  {
    field: 'tt_nt',
    headerName: 'Phân bổ',
    width: 150,
    type: 'number',
    align: 'right',
    renderCell: params => formatRoundedNumber((params.row as InvoiceAllocationRow).tt_nt)
  },
  {
    field: 'tt',
    headerName: 'Phân bổ VND',
    width: 150,
    type: 'number',
    align: 'right',
    renderCell: params => formatRoundedNumber((params.row as InvoiceAllocationRow).tt)
  },
  {
    field: 'cl_nt',
    headerName: 'Còn lại',
    width: 150,
    type: 'number',
    align: 'right',
    renderCell: params => formatRoundedNumber((params.row as InvoiceAllocationRow).cl_nt)
  },
  {
    field: 'tt_yn',
    headerName: 'Tất toán',
    width: 100,
    type: 'boolean',
    align: 'center',
    renderCell: params => {
      const row = params.row as InvoiceAllocationRow;

      return (
        <input
          type='checkbox'
          aria-label={`Tất toán hóa đơn ${row.so_ct}`}
          checked={row.tt_yn === 1}
          disabled
          className='size-4 accent-[#1976d2]'
        />
      );
    }
  },
  {
    field: 'han_tt',
    headerName: 'Hạn thanh toán',
    width: 120
  },
  {
    field: 'ngay_phai_tt',
    headerName: 'Ngày thanh toán',
    width: 120
  },
  {
    field: 'ma_ct',
    headerName: 'Chứng từ',
    width: 150
  }
];
